import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../Components/Common/breadcrumbs";
import {
  Box,
  Button,
  Typography,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { Plus, Layers, X, ShieldCheck } from "lucide-react";
import ProductCard from "./productCard";
import AddProduct from "../../Components/Common/productPopUp";
import DeleteModal from "../../Components/Common/deleteModal";
import Axios from "../../Common/api/axiosConfig";
import { uploadLink } from "../../utils/constants";
import CategoryManager from "../store-settings/CategoryManager";
import {
  CategoryItem,
  DEFAULT_CATEGORIES,
  getStoreCategories,
  saveCategoriesLocally,
} from "../../utils/categoryHelper";
import {
  enrichProductWithAmazonLink,
  setStoredAmazonLink,
  removeStoredAmazonLink,
} from "../../utils/amazonHelper";
import {
  enrichProductWithWarranty,
  setStoredWarrantyInfo,
  getStoredWarrantyInfo,
} from "../../utils/warrantyHelper";
import { onActiveStoreChange } from "../../utils/storeHelper";

export interface Product {
  _id: string;
  name: string;
  user_id: string;
  description: string;
  price: number;
  category?: string;
  images: string[];
  ishide?: boolean;
  inStock?: boolean;
  in_stock?: boolean;
  amazonLink?: string;
  amazon_link?: string;
  hasWarranty?: boolean;
  has_warranty?: boolean;
  warrantyPeriod?: string;
  warranty_period?: string;
  createdAt: string;
  updatedAt: string;
}

const ManageProducts = () => {
  const navigate = useNavigate();
  const [showProduct, setShowProduct] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<"create" | "edit">("create");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Category State
  const [categories, setCategories] = useState<CategoryItem[]>(DEFAULT_CATEGORIES);
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [categorySaving, setCategorySaving] = useState(false);
  const [storeId, setStoreId] = useState<string | null>(null);

  // Fetch products from API
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await Axios.get("/product");

      if (response.status !== 200) {
        throw new Error(`Failed to fetch products: ${response.status}`);
      }

      const rawProducts = response.data.data || [];
      const enriched = rawProducts.map((p: any) =>
        enrichProductWithWarranty(enrichProductWithAmazonLink(p))
      );
      setProducts(enriched);
      setPhoneNumber(response.data?.store?.number || "");
      if (response.data?.store?._id) {
        setStoreId(response.data.store._id);
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching products:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    const loaded = getStoreCategories();
    setCategories(loaded);

    const unsubscribe = onActiveStoreChange(() => {
      fetchProducts();
    });
    return () => unsubscribe();
  }, []);

  // Save categories handler
  const handleSaveCategories = async () => {
    try {
      setCategorySaving(true);
      saveCategoriesLocally(categories, storeId || undefined);

      if (storeId) {
        const payload = new FormData();
        payload.append("categories", JSON.stringify(categories));
        await Axios.patch(`/store/${storeId}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      setShowCategoryDialog(false);
    } catch (err) {
      console.error("Error saving categories:", err);
      saveCategoriesLocally(categories, storeId || undefined);
      setShowCategoryDialog(false);
    } finally {
      setCategorySaving(false);
    }
  };

  // Process images to include uploadLink for images array
  const processProductImages = (product: Product): string[] => {
    const processedImages: string[] = [];

    if (product.images && product.images.length > 0) {
      product.images.forEach((image) => {
        if (image.startsWith("http")) {
          processedImages.push(image);
        } else {
          const fullImagePath = `${uploadLink}${image}`;
          processedImages.push(fullImagePath);
        }
      });
    }

    if (processedImages.length === 0) {
      processedImages.push("https://via.placeholder.com/300x180?text=No+Image");
    }

    return processedImages;
  };

  // Open Add Product drawer
  const handleAddProduct = () => {
    setEditMode("create");
    setSelectedProduct(null);
    setShowProduct(true);
  };

  // Edit Product handler
  const handleEditProduct = (product: Product) => {
    setEditMode("edit");
    setSelectedProduct(product);
    setShowProduct(true);
  };

  // Delete product handler
  const handleDeleteClick = (productId: string) => {
    setProductToDelete(productId);
    setShowDeleteModal(true);
  };

  // Confirm delete handler
  const handleConfirmDelete = async () => {
    if (!productToDelete) return;

    try {
      const response = await Axios.delete(`/product/${productToDelete}`);

      if (response.status === 200) {
        setProducts((prev) => prev.filter((p) => p._id !== productToDelete));
        setShowDeleteModal(false);
        setProductToDelete(null);
      } else {
        throw new Error("Failed to delete product");
      }
    } catch (err) {
      console.error("Error deleting product:", err);
      alert("Failed to delete product");
      setShowDeleteModal(false);
      setProductToDelete(null);
    }
  };

  // Close delete modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setProductToDelete(null);
  };

  // Submit form handler
  const handleSubmit = (data: any) => {
    const rawData = data?.data || data;
    const enriched = enrichProductWithWarranty(
      enrichProductWithAmazonLink(rawData)
    );

    if (editMode === "edit" && selectedProduct) {
      setProducts((prev) =>
        prev.map((p) =>
          p._id === selectedProduct._id ? { ...p, ...enriched } : p
        )
      );
    } else {
      const newProduct: Product = {
        ...enriched,
        _id: enriched._id || Date.now().toString(),
        user_id: enriched.user_id || "current-user-id",
        createdAt: enriched.createdAt || new Date().toISOString(),
        updatedAt: enriched.updatedAt || new Date().toISOString(),
      };
      setProducts((prev) => [...prev, newProduct]);
    }
    setShowProduct(false);
  };

  // Update Amazon link handler
  const handleUpdateAmazonLink = async (
    productId: string,
    amazonLink: string
  ) => {
    const trimmed = (amazonLink || "").trim();

    // 1. Update state immediately for instant response
    setProducts((prev) =>
      prev.map((p) =>
        p._id === productId
          ? { ...p, amazonLink: trimmed, amazon_link: trimmed }
          : p
      )
    );

    // 2. Persist in local storage
    if (trimmed) {
      setStoredAmazonLink(productId, trimmed);
    } else {
      removeStoredAmazonLink(productId);
    }

    // 3. Persist to API
    try {
      const payload = new FormData();
      payload.append("amazonLink", trimmed);
      payload.append("amazon_link", trimmed);
      await Axios.patch(`/product/${productId}`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (err) {
      console.warn("Could not save amazonLink to API (stored locally):", err);
    }
  };

  // Toggle In Stock handler
  const handleToggleStock = async (productId: string, inStock: boolean) => {
    setProducts((prev) =>
      prev.map((p) =>
        p._id === productId ? { ...p, inStock, in_stock: inStock } : p
      )
    );
    try {
      const payload = new FormData();
      payload.append("inStock", inStock ? "true" : "false");
      await Axios.patch(`/product/${productId}`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (err) {
      console.error("Could not update inStock status to API:", err);
    }
  };

  // Toggle Warranty handler
  const handleToggleWarranty = async (
    productId: string,
    hasWarranty: boolean
  ) => {
    const existing = getStoredWarrantyInfo(productId);
    const warrantyPeriod =
      existing?.warrantyPeriod || (hasWarranty ? "1 Year Warranty" : "");

    // 1. Update state immediately
    setProducts((prev) =>
      prev.map((p) =>
        p._id === productId
          ? {
              ...p,
              hasWarranty,
              has_warranty: hasWarranty,
              warrantyPeriod: p.warrantyPeriod || warrantyPeriod,
              warranty_period: p.warranty_period || warrantyPeriod,
            }
          : p
      )
    );

    // 2. Persist in local storage
    setStoredWarrantyInfo(productId, {
      hasWarranty,
      warrantyPeriod: warrantyPeriod || "1 Year Warranty",
    });

    // 3. Persist to API
    try {
      const payload = new FormData();
      payload.append("hasWarranty", hasWarranty ? "true" : "false");
      payload.append("has_warranty", hasWarranty ? "true" : "false");
      if (hasWarranty && warrantyPeriod) {
        payload.append("warrantyPeriod", warrantyPeriod);
        payload.append("warranty_period", warrantyPeriod);
      }
      await Axios.patch(`/product/${productId}`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (err) {
      console.warn("Could not save warranty status to API (stored locally):", err);
    }
  };

  // Filter products by category
  const filteredProducts = products.filter((p) => {
    if (selectedCategory === "All") return true;
    const cat = p.category?.toLowerCase().trim() || "";
    const sel = selectedCategory.toLowerCase().trim();
    return (
      cat === sel ||
      cat.includes(sel) ||
      p.name.toLowerCase().includes(sel) ||
      (p.description && p.description.toLowerCase().includes(sel))
    );
  });

  if (loading) {
    return (
      <>
        <Breadcrumbs />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <Typography>Loading products...</Typography>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Breadcrumbs />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <Typography color="error">Error: {error}</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <Breadcrumbs />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 2.5,
          gap: { xs: 2, sm: 0 },
        }}
      >
        {/* Title Section */}
        <Box sx={{ mt: { xs: 2, sm: 3 } }}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Product Catalog
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your store products and categories
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignSelf: { xs: "stretch", sm: "auto" } }}>
          <Button
            variant="outlined"
            startIcon={<ShieldCheck size={18} />}
            onClick={() => navigate("/warranty-registrations")}
            sx={{
              borderRadius: 3,
              borderColor: "#16a34a",
              color: "#16a34a",
              textTransform: "none",
              fontSize: { xs: "0.85rem", sm: "0.95rem" },
              fontWeight: 600,
              px: { xs: 2, sm: 2.5 },
              py: { xs: 0.6, sm: 0.8 },
              "&:hover": {
                borderColor: "#15803d",
                bgcolor: "rgba(22,163,74,0.04)",
              },
            }}
          >
            Warranty Registrations
          </Button>

          <Button
            variant="outlined"
            startIcon={<Layers size={18} />}
            onClick={() => setShowCategoryDialog(true)}
            sx={{
              borderRadius: 3,
              borderColor: "#2563eb",
              color: "#2563eb",
              textTransform: "none",
              fontSize: { xs: "0.85rem", sm: "0.95rem" },
              fontWeight: 600,
              px: { xs: 2, sm: 2.5 },
              py: { xs: 0.6, sm: 0.8 },
              "&:hover": {
                borderColor: "#1d4ed8",
                bgcolor: "rgba(37,99,235,0.04)",
              },
            }}
          >
            Manage Categories
          </Button>

          <Button
            variant="contained"
            startIcon={<Plus />}
            onClick={handleAddProduct}
            sx={{
              borderRadius: 3,
              background: "linear-gradient(135deg, #2196f3, #42a5f5)",
              color: "#fff",
              boxShadow: "0px 4px 6px rgba(0,0,0,0.2)",
              textTransform: "none",
              fontSize: { xs: "0.85rem", sm: "0.95rem" },
              fontWeight: 600,
              px: { xs: 2, sm: 2.5 },
              py: { xs: 0.6, sm: 0.8 },
            }}
          >
            Add Product
          </Button>
        </Box>
      </Box>

      {/* Category Filter Chips */}
      {categories.some((c) => c.enabled !== false) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 3,
            overflowX: "auto",
            pb: 1,
            "&::-webkit-scrollbar": { height: 4 },
            "&::-webkit-scrollbar-thumb": { bgcolor: "#e2e8f0", borderRadius: 4 },
          }}
        >
          <Chip
            label={`All (${products.length})`}
            onClick={() => setSelectedCategory("All")}
            color={selectedCategory === "All" ? "primary" : "default"}
            variant={selectedCategory === "All" ? "filled" : "outlined"}
            sx={{ fontWeight: 600, cursor: "pointer" }}
          />
          {categories
            .filter((cat) => cat.enabled !== false)
            .map((cat) => {
              const count = products.filter((p) => {
                const c = p.category?.toLowerCase().trim() || "";
                const target = cat.name.toLowerCase().trim();
                return (
                  c === target ||
                  c.includes(target) ||
                  p.name.toLowerCase().includes(target)
                );
              }).length;
              const isSelected = selectedCategory === cat.name;

              return (
                <Chip
                  key={cat.id || cat.name}
                  label={`${cat.name} (${count})`}
                  onClick={() => setSelectedCategory(cat.name)}
                  color={isSelected ? "primary" : "default"}
                  variant={isSelected ? "filled" : "outlined"}
                  sx={{
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                />
              );
            })}
        </Box>
      )}

      {/* Product Cards */}
      {filteredProducts.length === 0 ? (
        <Box sx={{ textAlign: "center", mt: 4, py: 6, bgcolor: "#f8fafc", borderRadius: 3, border: "1px dashed #cbd5e1" }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            {selectedCategory === "All"
              ? "No products found. Add your first product!"
              : `No products found in category "${selectedCategory}".`}
          </Typography>
          {selectedCategory !== "All" && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setSelectedCategory("All")}
              sx={{ mt: 1, textTransform: "none" }}
            >
              View All Products
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredProducts.map((product) => (
            <Grid item xs={12} sm={6} md={4} key={product._id}>
              <Box sx={{ width: "100%" }}>
                <ProductCard
                  product={{
                    ...product,
                    images: processProductImages(product),
                  }}
                  phoneNumber={phoneNumber}
                  onEdit={() => handleEditProduct(product)}
                  onDelete={() => handleDeleteClick(product._id)}
                  onUpdateAmazonLink={handleUpdateAmazonLink}
                  onToggleStock={handleToggleStock}
                  onToggleWarranty={handleToggleWarranty}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Drawer for Add/Edit Product */}
      <AddProduct
        open={showProduct}
        onClose={() => setShowProduct(false)}
        mode={editMode}
        fetchProducts={fetchProducts}
        productData={selectedProduct}
        onSubmit={handleSubmit}
        categories={categories}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={showDeleteModal}
        onDeleteEvent={handleConfirmDelete}
        onCloseEvent={handleCloseDeleteModal}
      />

      {/* Manage Categories Modal Dialog */}
      <Dialog
        open={showCategoryDialog}
        onClose={() => setShowCategoryDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
            pt: 2.5,
            px: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Manage Store Categories
          </Typography>
          <IconButton onClick={() => setShowCategoryDialog(false)} size="small">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <CategoryManager
            categories={categories}
            onChange={(updated) => {
              setCategories(updated);
              saveCategoriesLocally(updated, storeId || undefined);
            }}
            showSectionSwitch={false}
            onSave={handleSaveCategories}
            isSaving={categorySaving}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ManageProducts;
